import { AutoAwesome } from "@mui/icons-material";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import { Box, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
    fetchCandidates,
    useCandidates,
} from "../../../../hooks/useCandidates";
import CCService from "../../../../services/httpServiceWithoutSuperAdmin";
import SideNavigationBar from "../../../reusables/SideNavigationBar/SideNavigationBar";
import CandidatePage from "./CandidatePage";
import CandidatesLoading from "./CandidatesLoading";
import PromptModal from "./PromptModal";

const CandidateList = () => {
    const BATCH_SIZE = 8;
    const service = new CCService();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const aiObserverRef = useRef(null);
    const [searchParams] = useSearchParams();
    const jobId = searchParams.get("jobId");
    const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "all");
    const [query, setQuery] = useState("");
    const [searchResponse, setSearchResponse] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [currentBatch, setCurrentBatch] = useState(0);
    const [hasMoreBatches, setHasMoreBatches] = useState(false);
    const [isLoadingBatch, setIsLoadingBatch] = useState(false);
    const [isAiSearchMode, setIsAiSearchMode] = useState(false);
    const [openPromptModal, setOpenPromptModal] = useState(false);
    const [savedCandidates, setSavedCandidates] = useState([]);
    const [loadingSaved, setLoadingSaved] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
        error,
        isPlaceholderData,
    } = useCandidates({
        job_id: jobId,
        tab: activeTab,
        enabled: true,
    });

    const candidates = useMemo(() => {
        if (activeTab === "saved") {
            return savedCandidates;
        }
        if (!data?.pages) return [];
        return data.pages.flatMap((page) => page.data);
    }, [data?.pages, activeTab, savedCandidates]);

    useEffect(() => {
        if (activeTab === "saved" && jobId) {
            fetchSavedCandidates();
        }
    }, [activeTab, jobId]);

    const fetchSavedCandidates = async () => {
        setLoadingSaved(true);
        try {
            const storageKey = `saved_candidates_${jobId}`;
            const savedUserIds = JSON.parse(localStorage.getItem(storageKey) || "[]");

            if (savedUserIds.length === 0) {
                setSavedCandidates([]);
                setLoadingSaved(false);
                return;
            }
            const params = new URLSearchParams();
            params.set("page_size", savedUserIds.length.toString());
            params.set("page_no", "1");
            params.set("job_id", jobId.toString());
            params.set("user_ids", JSON.stringify(savedUserIds));

            const response = await service.get(
                `/cee/recruiter/applied-candidates?${params.toString()}`
            );

            setSavedCandidates(response.data || []);
        } catch (error) {
            console.error("Error fetching saved candidates:", error);
            setSavedCandidates([]);
        } finally {
            setLoadingSaved(false);
        }
    };

    const handleBookmarkChange = (userId, isBookmarked) => {
        if (activeTab === "saved" && !isBookmarked) {
            fetchSavedCandidates();
        }
    };

    const handlePromptUpdate = (newHistory) => {
        setSearchResponse((prev) =>
            prev ? { ...prev, history: newHistory } : prev
        );
        setOpenPromptModal(false);
        setQuery("");
        handleSearch(newHistory);
    };

    const handleTabChange = (tab) => {
        const params = searchParams;
        setActiveTab(tab);
        params.set("tab", tab);
        navigate(`/AllRequests/Candidates?${params.toString()}`);
    };

    const tabs = [
        {
            key: "all",
            label: (
                <div className="!flex !items-center !gap-2 !text-sm md:text-base !whitespace-nowrap">
                    <AutoAwesome
                        className="!text-secondary"
                        sx={{ fontSize: { xs: "18px", md: "20px" } }}
                    />
                    Best Matches
                </div>
            ),
        },
        {
            key: "saved",
            label: (
                <div className="!flex !items-center !gap-2 !text-sm md:text-base">
                    <BookmarkBorderOutlinedIcon
                        sx={{
                            color: activeTab === "saved" ? "#1C8EA8" : "#666666",
                            fontSize: { xs: "18px", md: "20px" },
                            cursor: "pointer",
                        }}
                    />
                    Saved
                </div>
            ),
        },
    ];

    const downloadSavedCandidates = async () => {
        setIsDownloading(true);
        try {
            const candidateDetailsPromises = savedCandidates.map(
                async (candidate) => {
                    try {
                        const response = await service.get(
                            `/user/userDetailByUserId?user_id=${candidate.user_id}&is_email_required=true`
                        );
                        const userData = response.userDetail;
                        return {
                            full_name: candidate.full_name || "",
                            profile_pic: candidate.profile_pic || "",
                            email_id: userData?.email_id || "",
                            public_profile_link: userData?.username
                                ? `${process.env.REACT_APP_FRONTEND_URL}/candidates/${userData.username}`
                                : "",
                        };
                    } catch (error) {
                        console.error(
                            `Error fetching details for user ${candidate.user_id}:`,
                            error
                        );
                        return {
                            full_name: candidate.full_name || "",
                            profile_pic: candidate.profile_pic || "",
                            email_id: "",
                            public_profile_link: "",
                        };
                    }
                }
            );

            const candidateDetails = await Promise.all(candidateDetailsPromises);

            const worksheet = XLSX.utils.json_to_sheet(candidateDetails);

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Saved Candidates");

            const timestamp = new Date().toISOString().split("T")[0];
            const filename = `saved_candidates_job_${jobId}_${timestamp}.xlsx`;
            XLSX.writeFile(workbook, filename);
        } catch (error) {
            console.error("Error downloading candidates:", error);
            alert("Failed to download candidates. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    const allKeys = ["all", "saved"];
    const otherKeys = allKeys.filter((key) => key !== activeTab);

    const handleSearch = async (newHistory) => {
        setIsSearching(true);
        setIsAiSearchMode(true);
        try {
            const requestBody = {
                new_message: query ? query : undefined,
            };
            if (searchResponse) {
                if (newHistory) {
                    requestBody.chat_history = newHistory;
                } else {
                    requestBody.chat_history = searchResponse.history;
                }
            }
            const response = await service.put(
                "/jobs/recruiter/ai-search-student",
                requestBody
            );

            if (response && response.data.data.user_ids) {
                if (response.data.data.user_ids.length === 0) {
                    const params = new URLSearchParams();
                    params.set("page_size", "8");
                    params.set("page_no", "1");
                    params.set("job_id", jobId.toString());
                    const res = await service.get(
                        `/cee/recruiter/best-candidates?${params.toString()}`
                    );

                    const data = res?.data || [];
                    queryClient.setQueryData(["candidates", jobId, "all"], (oldData) => ({
                        ...oldData,
                        pages: oldData.pages.map((page) => ({ ...page, data: data })),
                    }));
                    setSearchResponse(response.data.data);
                    setCurrentBatch(0);
                    setHasMoreBatches(false);
                } else {
                    setSearchResponse(response.data.data);
                    setCurrentBatch(0);
                    const firstBatch = await fetchCandidateBatch(
                        response.data.data.user_ids,
                        0
                    );
                    queryClient.setQueriesData(
                        { queryKey: ["candidates", jobId, "all"] },
                        (old) => {
                            if (!old) return old;
                            return {
                                ...old,
                                pages: old.pages.map((page) => ({
                                    ...page,
                                    data: firstBatch,
                                })),
                            };
                        }
                    );
                    setCurrentBatch(1);
                    setHasMoreBatches(BATCH_SIZE < response.data.data.user_ids.length);
                    setQuery("");
                }
            }
        } catch (error) {
            console.log("error", error);
        } finally {
            setIsSearching(false);
        }
    };

    const fetchCandidateBatch = async (userIds, batchIndex) => {
        const start = batchIndex * BATCH_SIZE;
        const end = start + BATCH_SIZE;
        const batchUserIds = userIds.slice(start, end);
        if (batchUserIds.length === 0) {
            return [];
        }

        const params = new URLSearchParams();
        params.set("page_size", BATCH_SIZE.toString());
        params.set("page_no", "1");
        params.set("job_id", jobId.toString());
        params.set("user_ids", JSON.stringify(batchUserIds));

        const response = await service.get(
            `/cee/recruiter/applied-candidates?${params.toString()}`
        );

        return response.data;
    };

    const loadNextBatch = async () => {
        if (isLoadingBatch) {
            return;
        }

        setIsLoadingBatch(true);
        try {
            const nextBatchData = await fetchCandidateBatch(
                searchResponse.user_ids,
                currentBatch
            );
            const totalUsers = (searchResponse?.user_ids || []).length;
            const currentBatchEnd = (currentBatch + 1) * BATCH_SIZE;

            if (nextBatchData.length > 0) {
                queryClient.setQueriesData(
                    { queryKey: ["candidates", jobId, "all"] },
                    (old) => {
                        if (!old || !old.pages) return old;

                        return {
                            ...old,
                            pages: old.pages.map((page, index) => {
                                if (index === 0) {
                                    return {
                                        ...page,
                                        data: [...page.data, ...nextBatchData],
                                    };
                                }
                                return page;
                            }),
                        };
                    }
                );

                setCurrentBatch((prev) => prev + 1);
                const stillHasMore = currentBatchEnd < totalUsers;
                setHasMoreBatches(stillHasMore);
            } else {
                setHasMoreBatches(false);
            }
        } catch (error) {
            console.error("Error loading batch:", error);
            setHasMoreBatches(false); // Stop on error
        } finally {
            setIsLoadingBatch(false);
        }
    };

    const aiSentinelRef = useCallback(
        (node) => {
            // Disconnect previous observer
            if (aiObserverRef.current) {
                aiObserverRef.current.disconnect();
            }

            // Don't set up observer if conditions aren't met
            if (
                isLoadingBatch ||
                !hasMoreBatches ||
                !searchResponse ||
                activeTab !== "all"
            ) {
                return;
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreBatches && !isLoadingBatch) {
                        loadNextBatch();
                    }
                },
                { threshold: 0.5 }
            );

            if (node) {
                observer.observe(node);
                aiObserverRef.current = observer;
            }
        },
        [
            isLoadingBatch,
            hasMoreBatches,
            currentBatch,
            searchResponse,
            activeTab,
            loadNextBatch,
        ]
    );

    // Add cleanup effect
    useEffect(() => {
        return () => {
            if (aiObserverRef.current) {
                aiObserverRef.current.disconnect();
            }
        };
    }, []);

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    useEffect(() => {
        if (!jobId) return;
        otherKeys.forEach(async (key) => {
            await queryClient.prefetchInfiniteQuery({
                queryKey: ["candidates", jobId, key],
                queryFn: () =>
                    fetchCandidates({ pageParam: 1, job_id: jobId, tab: "all" }),
                initialPageParam: 1,
                staleTime: 30 * 60 * 1000,
                gcTime: 60 * 60 * 1000,
            });
        });
    }, [jobId, queryClient]);

    if (!jobId) {
        return <div>No job selected</div>;
    }
    if (activeTab === "Saved" && savedCandidates.length === 0) {
        return <div>No saved candidates</div>;
    }
    if (isLoading) {
        return <CandidatesLoading />;
    }
    return (
        <Box sx={{ ml: 10, px: 2, py: 5 }}>
            <SideNavigationBar />

            <div className="!border-b-2 !border-gray-300 !mb-6 !w-full !overflow-x-auto !overflow-y-hidden !relative">
                <nav className="!-mb-px !flex !space-x-8">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => handleTabChange(tab.key)}
                            className={`!py-2 !px-1 !border-b-2 !text-[18px] !font-bold !whitespace-nowrap ${activeTab === tab.key
                                ? "!border-primary !text-primary"
                                : "!border-transparent !text-gray-500 !hover:border-gray-400"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
                {activeTab === "saved" && savedCandidates.length > 0 && (
                    <button
                        onClick={downloadSavedCandidates}
                        className="!bg-primary !text-white !mb-2 !py-2 !px-4 !rounded !cursor-pointer !hover:bg-primary/80 !absolute !right-4 !top-1/2 !-translate-y-1/2"
                    >
                        {isDownloading ? "Downloading..." : "Download Saved Candidates"}
                    </button>
                )}
            </div>
            <div className="">
                <CandidatePage
                    candidates={candidates}
                    job_id={jobId}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    isLoadingAiBatch={isLoadingBatch}
                    aiSentinelRef={aiSentinelRef}
                    aiSearchMode={isAiSearchMode}
                    searchResponse={searchResponse}
                    onBookmarkChange={handleBookmarkChange}
                    activeTab={activeTab}
                />
            </div>

            {activeTab === "all" && (
                <div className="!fixed !left-4 !right-4 !md:left-12 !md:right-12 !bottom-6 !z-20">
                    <div className="!max-w-7xl !mx-auto">
                        <div
                            style={{
                                boxShadow: "0 0 30px 0px rgba(28, 142, 168, 0.8)",
                            }}
                            className="bg-white border-2 border-grey-light rounded-xl shadow-xl"
                        >
                            <div className="!p-3 !flex !items-center !gap-3">
                                <div className="!flex-1 !min-w-0">
                                    <label className="sr-only">AI Search</label>
                                    <input
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        onKeyDown={onKeyDown}
                                        placeholder="Want best search results? Just explain more about the profile you need in simple language."
                                        className="!w-full !bg-transparent !outline-none !text-sm !md:text-base !px-3 !py-2 !placeholder:text-[#A8A8A8] !placeholder:text-16"
                                    />
                                </div>
                                <div className="!flex !items-center !gap-3">
                                    <button
                                        onClick={() => handleSearch()}
                                        disabled={isSearching}
                                        className="!flex !items-center !gap-2 !px-4 !py-2 !rounded-md !border !border-primary1 !bg-primary1 !text-white !text-sm !shadow-sm !hover:brightness-95 !disabled:opacity-60"
                                    >
                                        <Search size={20} />
                                        <span>{isSearching ? "Searching..." : "AI Search"}</span>
                                    </button>
                                </div>
                            </div>
                            <div className="!border-t !border-[#E0E0E0]">
                                {searchResponse?.history.length > 0 &&
                                    searchResponse?.summary && (
                                        <div className="!p-3 !mx-3">
                                            <p className="!text-xs !font-semibold !text-grey-600 !mb-1">
                                                AI Understanding of Your Search
                                            </p>
                                            <div className="!flex !items-center">
                                                <ol className="!text-xs !text-primary !space-y-1 !min-w-0 !flex-1">
                                                    <li className="!flex !items-center !gap-x-2">
                                                        <Tooltip
                                                            title={searchResponse?.summary?.summary || ""}
                                                            arrow
                                                        >
                                                            <span className="!whitespace-nowrap !overflow-hidden !text-ellipsis">
                                                                • {searchResponse?.summary?.summary}
                                                            </span>
                                                        </Tooltip>
                                                        <span
                                                            onClick={() => setOpenPromptModal(true)}
                                                            className="!cursor-pointer !font-semibold !text-primary !underline !whitespace-nowrap"
                                                        >
                                                            Edit Search Filtering
                                                        </span>
                                                    </li>
                                                </ol>
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <PromptModal
                open={openPromptModal}
                history={searchResponse?.history || []}
                onClose={() => setOpenPromptModal(false)}
                onUpdate={handlePromptUpdate}
            />
        </Box>
    );
};

export default CandidateList;
